import { c as _c } from "react-compiler-runtime";
import { useCallback, useMemo } from 'react';
import { useCrmObjectType, useFiltersStateStore } from 'framework-listing-lib/internal/providers/CrmObjectTypeListingClientProvider';
import { useFilterGroups } from './useListingLibFilters';
import useQuickFilters from './useQuickFilters';
import useResetFilters from './useResetFilters';
import useHasNonHiddenFilters from './useHasNonHiddenFilters';
import useRemovePropertyFilter from './useRemovePropertyFilter';
import useUpdateFilters from './useUpdateFilters';
/**
 * @deprecated Use focused hooks instead. For reading filter state, prefer hooks from
 * `useListingLibFilters` (e.g. `useFiltersState`, `useFilterGroups`, `useInitialFilter`).
 * For specific actions:
 * @see useResetFilters — reset filters to their default or view state
 * @see useHasNonHiddenFilters — check whether any filters are currently active
 * @see useRemovePropertyFilter — remove all filter operators for a single property
 * @see usePagination — read current pagination state
 * @see useUpdateFilters — update the current filter state
 *
 * This hook reads the global filter from CrmObjectTypeListingClientProvider and provides two
 * main methods to manipulate filters in the codebase:
 *  - onUpdateFilters: to update/override the filter state.
 *  - onDeleteObject: to delete & ignore objectIds from upcoming requests to crm-search.
 *
 * It also propagates current applied filters as well as list of deleted objects.
 *
 * Components & hooks that need to read/write filter state should pull the filter information from this hook (useCrmObjectTypeListingFilters).
 *
 */
function useCrmObjectTypeListingFilters() {
  "use memo";

  const $ = _c(20);
  const {
    hiddenFilter,
    isFilterFromCustomView,
    initialFilter
  } = useCrmObjectType();
  const onSetDeletedObjects = useFiltersStateStore(_temp);
  const filtersState = useFiltersStateStore(_temp2);
  const filterGroups = useFilterGroups();
  const quickFilters = useQuickFilters();
  const deletedObjects = useFiltersStateStore(_temp3);
  const hasAppliedFilters = useHasNonHiddenFilters();
  const onRemovePropertyFilter = useRemovePropertyFilter();
  const {
    onUpdateFilters
  } = useUpdateFilters();
  const onClearFilters = useResetFilters();
  let t0;
  if ($[0] !== onSetDeletedObjects) {
    t0 = () => {
      onSetDeletedObjects([]);
    };
    $[0] = onSetDeletedObjects;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onClearDeletedObjects = t0;
  let t1;
  if ($[2] !== onSetDeletedObjects) {
    t1 = objectIdOrIds => {
      if (Array.isArray(objectIdOrIds)) {
        onSetDeletedObjects(prevDeletedObjects => [...prevDeletedObjects, ...objectIdOrIds]);
      } else {
        onSetDeletedObjects(prevDeletedObjects_0 => [...prevDeletedObjects_0, objectIdOrIds]);
      }
    };
    $[2] = onSetDeletedObjects;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onDeleteObject = t1;
  let t2;
  if ($[4] !== filterGroups || $[5] !== filtersState || $[6] !== quickFilters) {
    t2 = Object.assign({}, filtersState, {
      filterGroups,
      quickFilters
    });
    $[4] = filterGroups;
    $[5] = filtersState;
    $[6] = quickFilters;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== deletedObjects || $[9] !== hasAppliedFilters || $[10] !== hiddenFilter || $[11] !== initialFilter || $[12] !== isFilterFromCustomView || $[13] !== onClearDeletedObjects || $[14] !== onClearFilters || $[15] !== onDeleteObject || $[16] !== onRemovePropertyFilter || $[17] !== onUpdateFilters || $[18] !== t2) {
    t3 = {
      deletedObjects,
      filters: t2,
      hasAppliedFilters,
      hiddenFilter,
      initialFilter,
      isFilterFromCustomView,
      onClearDeletedObjects,
      onClearFilters,
      onDeleteObject,
      onRemovePropertyFilter,
      onResetFilters: onClearFilters,
      onUpdateFilters
    };
    $[8] = deletedObjects;
    $[9] = hasAppliedFilters;
    $[10] = hiddenFilter;
    $[11] = initialFilter;
    $[12] = isFilterFromCustomView;
    $[13] = onClearDeletedObjects;
    $[14] = onClearFilters;
    $[15] = onDeleteObject;
    $[16] = onRemovePropertyFilter;
    $[17] = onUpdateFilters;
    $[18] = t2;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const value = t3;
  return value;
}
function _temp3(state_1) {
  return state_1.deletedObjects;
}
function _temp2(state_0) {
  return state_0.filtersState;
}
function _temp(state) {
  return state.setDeletedObjects;
}
export default useCrmObjectTypeListingFilters;